import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSortBy, useTable } from 'react-table';

import DotsLoader from '@/Components/Loader/DotsLoader';

import { fileTypeSVG } from '@/Assets/icons';
import showToast from '@/Components/Toast/showToastTemplate';
import Icon from '@/Components/UI/Icon/Icon';
import { setIsRequestingRows } from '@/redux/reducers/spreadsheetSlice';
import { get } from '@/Utils/API';
import { sortDirection, TOAST_TYPE_ERROR } from '@/Utils/constants';
import { FILE_STATUS_PROCESSED } from '@/Utils/fileConstants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { parseFileSize } from '@/Utils/parseFileSize';
import { format } from 'date-fns';
import TextInput from '../UI/Form/TextInput/TextInput';

const OWNED_BY_ME = 'owned-by-me';
const SHARED_WITH_ME = 'shared-with-me';
const SHARED_WITH_MY_TEAM = 'shared-with-my-team';

const LookupChooseFile = ({ setSelectedFile, closeModal }) => {
  const [initialData, setInitialData] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultLoading, setSearchResultLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFolder, setCurrentFolder] = useState(null);
  const [parentFolder, setParentFolder] = useState(null);

  const getFilesFromSource = async (endpoint, source) => {
    let response = null;
    try {
      response = await get(endpoint);
      return response.map((e) => ({
        ...e.metadata,
        source,
      }));
    } catch (error) {
      if (response?.Message !== 'User is not a part of a team') {
        showToast({
          type: TOAST_TYPE_ERROR,
          text: 'Error fetching files. Please try again later.',
          errorContext: error,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        });
      }
      return []; // Return an empty array if the request fails
    }
  };

  const getData = useCallback(async (folderHandle = '', source = '', parentFolderHandle = null) => {
    try {
      setSearchResultLoading(true);
      let files = [];

      if (!folderHandle) {
        // Root level, fetch files from all sources
        const filesOwnedByMe = await getFilesFromSource('library/', OWNED_BY_ME);
        const filesSharedWithMe = await getFilesFromSource(
          `library/${SHARED_WITH_ME}/`,
          SHARED_WITH_ME
        );
        const filesSharedWithMyTeam = await getFilesFromSource(
          `library/${SHARED_WITH_MY_TEAM}/`,
          SHARED_WITH_MY_TEAM
        );

        files = [...filesOwnedByMe, ...filesSharedWithMe, ...filesSharedWithMyTeam];
        setParentFolder(null); // No parent folder if in the root
      } else {
        // Fetch files inside a folder based on its source
        const folderEndpoint =
          source === SHARED_WITH_ME
            ? `library/${SHARED_WITH_ME}/${folderHandle}`
            : source === SHARED_WITH_MY_TEAM
            ? `library/${SHARED_WITH_MY_TEAM}/${folderHandle}`
            : `library/${folderHandle}`;

        const filesInFolder = await getFilesFromSource(folderEndpoint, source || OWNED_BY_ME);
        files = [...files, ...filesInFolder];
        setParentFolder(parentFolderHandle); // Set the parent folder handle
      }

      const uuidOfFileToExclude = getFileUuidFromPath();
      const filterFiles = files.filter(
        (el) =>
          el.Status === FILE_STATUS_PROCESSED &&
          (el.FileRows || el.IsDirectory) &&
          el.Deleting === 0 &&
          el.FileUuid !== uuidOfFileToExclude
      );
      setInitialData(filterFiles);
      setSearchResults(filterFiles);
    } catch (error) {
      showToast({
        type: TOAST_TYPE_ERROR,
        text: 'Error fetching files. Please try again later.',
        errorContext: error,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    } finally {
      setSearchResultLoading(false);
    }
  }, []);

  const data = useMemo(() => searchResults || [], [searchResults]);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'FileName',
        Cell: ({ row }) => {
          if (row.original.IsDirectory) {
            return (
              <div className='flex gap-2'>
                <div className='w-4'>
                  <Icon name='folder-filled' />
                </div>
                {row.original.FileName}
              </div>
            );
          }
          return row.original.FileName;
        },
      },
      {
        Header: 'Rows',
        accessor: 'FileRows',
        Cell: ({ row }) => (row.original.IsDirectory ? '' : row.original.FileRows), // Show empty for folders
      },
      {
        Header: 'File Size',
        accessor: 'FileSize',
        Cell: ({ cell, row }) => (row.original.IsDirectory ? '' : parseFileSize(cell.value)), // Show empty for folders
      },
      {
        Header: 'Last Modified',
        accessor: 'LastUpdated',
        Cell: ({ row }) => {
          return format(new Date(row.values.LastUpdated), 'MMM d, yyyy - h:mm:ss a');
        },
      },
    ],
    []
  );

  const defaultSortBy = useMemo(
    () => [
      {
        id: 'LastUpdated',
        desc: true,
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: defaultSortBy,
      },
    },
    useSortBy
  );

  useEffect(() => {
    getData(); // Initially fetch root files
  }, [getData]);

  useEffect(() => {
    const results = initialData?.filter((el) =>
      el.FileName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(results);
  }, [initialData, searchTerm]);

  // Function to handle clicking a folder
  const onFolderClick = (folder) => {
    if (folder.IsDirectory) {
      // Fetch files inside the folder based on its source
      getData(folder.FileUuid, folder.source);
      setCurrentFolder(folder.FileUuid);
    } else {
      onSelectFile(folder);
    }
  };

  // Function to go back to the parent folder
  const goUpOneLevel = () => {
    if (parentFolder) {
      // If there's a parent folder, go up one level
      getData(parentFolder, currentFolderSource, null); // Fetch parent folder's content
      setCurrentFolder(parentFolder); // Set current folder to the parent folder
    } else {
      // If no parent folder, go back to the root
      getData();
      setCurrentFolder(null);
    }
  };

  const onSelectFile = (file) => {
    setSelectedFile(file);
    closeModal();
  };

  return (
    <div className='lookup-choose-file-container'>
      <fieldset>
        <TextInput
          className='mb-5'
          placeholder='Search'
          data-cy='lookup-choose-file-search-input'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </fieldset>
      {currentFolder && (
        <div className='mb-3 go-up-container'>
          <button className='go-up-button' onClick={goUpOneLevel}>
            <Icon name='arrow-up' className='inline-block' /> Go Up
          </button>
        </div>
      )}

      <div className='table-wrapper'>
        {searchResultLoading ? (
          <div className='lookupLoader'>
            <DotsLoader setIsRequestingRows={setIsRequestingRows} />
          </div>
        ) : (
          <table {...getTableProps()} className='table w-full'>
            <thead>
              {headerGroups.map((headerGroup, idx) => (
                <tr key={idx} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, idx) => (
                    <th
                      key={idx}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className={`table-header ${
                        column.isSorted
                          ? column.isSortedDesc
                            ? sortDirection.descending
                            : sortDirection.ascending
                          : ''
                      }`}
                    >
                      {column.render('Header')}
                      <span>
                        <div className={'arrow-up'} />
                        <div className={'arrow-down'} />
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, idx) => {
                prepareRow(row);
                return (
                  <tr key={idx} {...row.getRowProps()} onClick={() => onFolderClick(row.original)}>
                    {row.cells.map((cell, idx) => {
                      return (
                        <td
                          key={idx}
                          className='overflow-hidden whitespace-nowrap text-ellipsis font-medium mb-0 pl-[33px]'
                          {...cell.getCellProps()}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LookupChooseFile;
